import { useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Eye, FileSpreadsheet, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import JobImportPlan from "@/components/JobImportPlan";
import { SubTabs } from "@/components/stores/storesUi";

// The Planning sheet's heading row, column for column (Medpack FY 2026-27.xlsx, row 3).
// "=" is the sheet's own heading for the substrate name next to Substrate Code.
const PLANNING_HEADERS = [
  "Sl.No", "Schedule given", "BPR Number", "Date of Order Received", "BPR Issue Date", "Customer Name",
  "Order Qty", "Unit", "REMARKS FOR TEAM", "Item Description", "Product Code", "Sap Code",
  "Order Confirmed BY", "PO Date", "PO Number", "Label Length Along", "Label Width Across", "Die Size",
  "Job Type", "Substrate Code", "=", "GSM", "Supplier", "No of Colors", "Colour Details", "Print Type",
  "Color Matching", "Finishing Details", "Varnish Type", "Embellishment", "Form of Supply", "Shade Card",
  "No of labels per Roll", "Labels per Sheet", "Numbering", "Core Diameter", "Job Due Date", "Direction",
  "Add .Info if any", "Teeth", "Cylinder in mm", "Stock Wdith (mm)", "Assigned width (Stores)",
  "Ups in Across", "Ups in Along", "Total ups in 1 Repeat", "Ups in Die", "Repeat label Along gap(mm)",
  "Plate Box Number", "Die Box Number", "Die Cutting", "Planned Good Meter", "Planned Waste Meter",
  "Excess Qty+ AVT +Slitting wasatge", "Makeready Time (hrs)", "Print .Time (hrs)", "Total Time",
  "Plate Availability", "Die Availability", "Material Availability", "Ink Availability",
  "Spl Winding Instructions", "Proof Aproved on", "Print format Adjustment", "Work Order Generated date",
  "Spl Instruction 1", "Spl Instruction 2", "Spl Instruction 3", "BPR/Work order Issued by",
  "Delivery Location", "Delivery Area", "Dispatch Status",
];
const PLANNING_HEADER_LINE = PLANNING_HEADERS.join("\t");

/**
 * Jobs from the Planning sheet: upload the workbook (only its Planning sheet is read) or
 * paste rows copied from it. Preview first, then import. Rows merge into jobs by BPR
 * number with the same rules as Smart Import (backend/routes/job_import.py), so the
 * sheet can be imported again whenever it changes. Open lists refresh by themselves.
 * `uploadOnly` hides the paste option.
 */
export default function PlanningImport({ uploadOnly = false }) {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "import"

  const ready = mode === "upload" ? !!file : !!text.trim();
  const reset = () => { setPreview(null); setResult(null); };

  const send = (step) => {
    if (mode === "upload") {
      const form = new FormData();
      form.append("file", file);
      return api.post(step === "preview" ? "/import/inspect" : "/import/auto", form, {
        params: { register: "planning" },
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.post(step === "preview" ? "/import/paste/inspect" : "/import/paste", { text, kind_hint: "planning" });
  };

  const run = async (step) => {
    setBusy(step);
    try {
      const { data } = await send(step);
      if (step === "preview") {
        setPreview(data);
        setResult(null);
      } else {
        setResult(data);
        setPreview(null);
        const c = data.jobs?.counts || {};
        toast.success(`${c.new || 0} new jobs · ${c.updated || 0} updated · ${(c.unchanged || 0).toLocaleString("en-IN")} already up to date`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const insertHeader = () => {
    if (!text.trim()) setText(PLANNING_HEADER_LINE + "\n");
    else if (!/bpr number/i.test(text.split("\n")[0])) setText(PLANNING_HEADER_LINE + "\n" + text);
    else toast.info("The heading row is already there");
    reset();
  };

  const counts = preview?.jobs?.counts;
  const nothingNew = counts && counts.new + counts.updated === 0;

  return (
    <div className="space-y-3" data-testid="planning-import">
      <p className="text-xs text-neutral-600 leading-relaxed">
        Jobs come from the <b>Planning</b> sheet of your <em>Medpack FY 2026-27</em> workbook. Rows are matched to
        jobs by <b>BPR Number</b>, so importing the sheet again never creates a second job, and anything changed in
        the ERP since the last import is kept.
      </p>

      {!uploadOnly && (
        <SubTabs value={mode} onChange={(m) => { setMode(m); reset(); }} testid="planning-mode"
          options={[{ value: "upload", label: "Upload the workbook" }, { value: "paste", label: "Paste Planning rows" }]} />
      )}

      {mode === "upload" ? (
        <div className="flex flex-wrap items-center gap-2">
          <FileSpreadsheet size={16} className="text-[#FF5A00]" />
          <input
            type="file"
            accept=".xlsx,.xlsm,.csv"
            data-testid="planning-file"
            onChange={(e) => { setFile(e.target.files?.[0] || null); reset(); }}
            className="text-xs file:mr-3 file:rounded-sm file:border file:border-neutral-300 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-neutral-700"
          />
          <span className="text-[11px] text-neutral-500">Only the Planning sheet is read; the other sheets are left alone.</span>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-600">
            <ClipboardPaste size={14} className="text-[#FF5A00]" />
            In the Planning sheet select the rows <b>with the heading row</b>, Ctrl+C, and paste below.
            <Button type="button" variant="outline" size="sm" className="rounded-sm h-7 ml-auto" onClick={insertHeader}
              data-testid="planning-insert-header">
              Insert Planning heading row
            </Button>
          </div>
          <Textarea
            data-testid="planning-textarea"
            value={text}
            onChange={(e) => { setText(e.target.value); reset(); }}
            placeholder="Paste rows from the Planning sheet here (Ctrl+V)…"
            rows={8}
            className="rounded-sm font-mono-num text-xs"
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => run("preview")}
          disabled={!ready || !!busy} data-testid="planning-preview">
          <Eye size={13} className="mr-1.5" /> {busy === "preview" ? "Reading…" : "Preview"}
        </Button>
      </div>

      {preview?.jobs && (
        <div className="border border-neutral-200 bg-white" data-testid="planning-preview-result">
          <JobImportPlan plan={preview.jobs} />
          <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex flex-wrap items-center justify-end gap-2">
            {nothingNew && <span className="mr-auto text-sm text-neutral-600">Nothing new: every job here is already up to date in the ERP.</span>}
            <Button type="button" size="sm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" onClick={() => run("import")}
              disabled={!!busy || nothingNew} data-testid="planning-import-btn">
              <Upload size={13} className="mr-1.5" />
              {busy === "import" ? "Importing…" : `Import · ${counts.new.toLocaleString("en-IN")} new, ${counts.updated.toLocaleString("en-IN")} updated`}
            </Button>
          </div>
        </div>
      )}

      {result?.jobs && (
        <div className="border border-emerald-200 bg-white" data-testid="planning-result">
          <div className="px-5 py-3 border-b border-emerald-200 bg-emerald-50/40 text-sm font-medium text-emerald-900">
            Import complete. The Jobs list and every open screen update by themselves.
          </div>
          <JobImportPlan plan={result.jobs} done />
        </div>
      )}
    </div>
  );
}
